import { Box, Button, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  /** When true, hides title (use when AppBar already shows page title) */
  compact?: boolean;
}

export function PageHeader({
  title,
  subtitle,
  action,
  secondaryAction,
  compact = false,
}: PageHeaderProps) {
  if (compact && !action && !secondaryAction && !subtitle) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 3,
      }}
    >
      {!compact && (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: 'text.primary' }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {compact && subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pt: { sm: 0.5 } }}>
          {subtitle}
        </Typography>
      )}
      {(action || secondaryAction) && (
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
          sx={{
            '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' } },
          }}
        >
          {secondaryAction}
          {action}
        </Stack>
      )}
    </Box>
  );
}

interface PrimaryActionButtonProps {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

export function PrimaryActionButton({
  label,
  icon,
  onClick,
  disabled,
}: PrimaryActionButtonProps) {
  return (
    <Button
      variant="contained"
      startIcon={icon}
      onClick={onClick}
      disabled={disabled}
      sx={{ whiteSpace: 'nowrap' }}
    >
      {label}
    </Button>
  );
}
